import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { graphql, compose } from "react-apollo";
import {
  queryGetCustomerDetail,
  queryGetLabelsByType,
  mutationAddFieldToCustomer,
  mutationAddNoteToCustomerInfo,
  mutationAddLabelToCustomer,
  mutationDeleteLabelFromCustomer
} from "Apollo";
import Avatar from "module-common/Avatar";
import FieldRow from "./FieldRow";
import TextArea from "module-common/TextArea";
import Button from "module-common/Button";
import LabelList from "module-common/label-list/LabelList";
import Timeline from "./Timeline";
import Picker from "./PickerFieldWithQuery";
import { ShowNotice } from "Store";

export default compose(
  graphql(queryGetCustomerDetail, {
    name: "customerDetail",
    options: ({ selectedId }) => ({ variables: { id: selectedId } })
  }),
  graphql(queryGetLabelsByType, {
    name: "labelsData",
    options: { variables: { type: "CUSTOMER" } }
  }),
  graphql(mutationAddFieldToCustomer, {
    name: "mutateCustomerField"
  }),
  graphql(mutationAddNoteToCustomerInfo, {
    name: "mutateCustomerNote"
  }),
  graphql(mutationAddLabelToCustomer, { name: "mutateAdd" }),
  graphql(mutationDeleteLabelFromCustomer, { name: "mutateDelete" })
)(
  class Detail extends Component {
    static propTypes = {
      selectedId: PropTypes.number
    };
    state = {
      image: "",
      customerName: "",
      channel: "",
      note: "",
      customerId: 0,
      labels: [],
      fields: [],
      journey: [],
      loading: false,
      editable: true
    };

    componentWillReceiveProps(nextProps) {
      const { customerDetail } = nextProps;
      const getCustomerDetail = customerDetail.getCustomerDetail;
      try {
        // Assign data for customer detail
        this.state.customerId = getCustomerDetail.id;
        this.state.image = getCustomerDetail.channelHandler.displayPicture;
        this.state.customerName = getCustomerDetail.name;
        this.state.channel = getCustomerDetail.channelHandler.channel.name;
        if (getCustomerDetail.noteInfo) {
          this.state.note = getCustomerDetail.noteInfo.content;
        }
        if (getCustomerDetail.fields) {
          this.state.fields = getCustomerDetail.fields;
        }
        if (getCustomerDetail.labels) {
          this.state.labels = getCustomerDetail.labels;
        }
      } catch (e) {}
    }

    render() {
      const {
        image,
        customerName,
        channel,
        note,
        labels,
        fields,
        loading,
        editable,
        customerId
      } = this.state;
      const {
        selectedId,
        labelsData: { getLabelTemplates: labelForPicker }
      } = this.props;

      const fieldRow = [];
      fieldRow.push(
        <FieldRow key="name" name="Name" value={customerName} disabled />
      );
      for (const row of fields) {
        fieldRow.push(
          <FieldRow
            key={row.id}
            customerId={customerId}
            fieldId={row.id}
            name={row.fieldType.displayName}
            value={row.value}
            disabled={!editable}
          />
        );
      }

      const labelRow = [];
      for (const label of labels) {
        if (label.labelTemplate.isShown) {
          labelRow.push({
            label: label.labelTemplate.displayName,
            value: label.labelTemplate,
            color: "#" + label.labelTemplate.color
          });
        }
      }

      const pickers = [];
      if (labelForPicker) {
        for (const label of labelForPicker) {
          pickers.push({
            label: label.displayName,
            color: "#" + label.color,
            value: label
          });
        }
      }

      const addNewFieldButton = editable && (
        <Picker onSelected={this.onSelected}>
          <Button size="medium" color="green">
            + Add new field
          </Button>
        </Picker>
      );

      return (
        <Wrapper>
          <Profile>
            <Title>Profile</Title>
            <LeftColumn>
              <AvatarWrapper
                image={image}
                channel={channel}
                isCustomChannel={true}
              />
            </LeftColumn>
            <RightColumn>
              {fieldRow}
              {addNewFieldButton}
            </RightColumn>
            <Label>
              <Title>Label</Title>
              <LabelList
                labels={labelRow}
                pickers={pickers}
                editable={true}
                onAdd={this.onAdd}
                onRemove={this.onRemove}
              />
            </Label>
            <Notes>
              <Title>Notes</Title>
              <TextAreaField
                value={note}
                onChange={e =>
                  this.setState({
                    note: e.target.value
                  })
                }
              />
              <StyledButton
                onClick={this.onSaveNote}
                loading={loading}
                size={"small"}
                width={50}
              >
                Save
              </StyledButton>
            </Notes>
          </Profile>
          <Activity>
            <Title>Activity</Title>
            <Timeline selectedId={selectedId} />
          </Activity>
        </Wrapper>
      );
    }
    onSelected = async selected => {
      const { mutateCustomerField, customerDetail } = this.props;
      const customer = customerDetail.getCustomerDetail;
      if (!customer) return;

      try {
        await mutateCustomerField({
          variables: {
            customerInput: { id: customer.id },
            customerFieldTypeInput: { id: selected.value.id },
            content: ""
          },
          refetchQueries: ["getCustomerDetail", "getCustomerJourney"]
        });
      } catch (error) {
        ShowNotice("customer-field-add-error");
      }
    };
    onSaveNote = async () => {
      const { mutateCustomerNote, customerDetail } = this.props;
      const customer = customerDetail.getCustomerDetail;
      if (customer) {
        this.state.loading = true;
        try {
          await mutateCustomerNote({
            variables: {
              customerInput: {
                id: customer.id
              },
              content: this.state.note
            },
            refetchQueries: ["getCustomerDetail", "getCustomerJourney"]
          });

          ShowNotice("note-save-success");
        } catch (error) {
          ShowNotice("note-save-error");
        }
        this.state.loading = false;
      }
    };

    onAdd = async label => {
      const { customerDetail, mutateAdd } = this.props;
      const customer = customerDetail.getCustomerDetail;
      try {
        await mutateAdd({
          variables: {
            customerInput: { id: customer.id },
            labels: [{ id: label.value.id }]
          },
          refetchQueries: ["getCustomerDetail", "getCustomerJourney"]
        });
        ShowNotice("label-add-success");
      } catch (error) {
        ShowNotice("label-add-error");
      }
    };

    onRemove = async label => {
      const { customerDetail, mutateDelete } = this.props;
      const customer = customerDetail.getCustomerDetail;
      try {
        await mutateDelete({
          variables: {
            customerInput: { id: customer.id },
            labels: [{ id: label.value.id }]
          },
          refetchQueries: ["getCustomerDetail", "getCustomerJourney"]
        });
        ShowNotice("label-delete-success");
      } catch (error) {
        ShowNotice("label-delete-error");
      }
    };
  }
);

const Wrapper = styled.div`
  display: flex;
`;

const Title = styled.div`
  flex: 1 100%;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 5px;
`;

const Profile = styled.div`
  width: 440px;
  display: flex;
  flex-wrap: wrap;
  padding: 0 10px;
  margin-right: 20px;
  align-content: flex-start;
`;
const Activity = styled.div`
  width: 100%;
  height: 500px;
  display: flex;
  flex-wrap: wrap;
  padding: 0 10px;
  align-content: flex-start;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 50px;
`;
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const AvatarWrapper = styled(Avatar)`
  width: 100px;
  height: 100px;
  border-radius: 100%;
  margin-right: 20px;
`;

const Label = styled.div`
  margin-top: 20px;
  flex: 1 100%;
  border-top: 1px solid #ddd;
`;

const Notes = styled.div`
  margin-top: 20px;
  flex: 1 100%;
  border-top: 1px solid #ddd;
`;

const TextAreaField = styled(TextArea)`
  width: 400px;
  height: 100px;
`;

const StyledButton = styled(Button).attrs({ rounded: true, width: 100 })`
  float: right;
`;



// WEBPACK FOOTER //
// ./src/module-customer-journey/detail/CustomerDetail.js